import type { FormEventHandler, FunctionComponent } from "react";
import { Modal as BModal } from "react-bootstrap";
import type { ButtonVariant } from "react-bootstrap/esm/types";
import Button from "@/components/Button";
import { FormPreventDefault } from "@/components/Form";
import type { ButtonProps } from "@/components/ui/button";

// map bootstrap button variant to shadcn Button variant (callers can still pass bootstrap API)
const mapCancelVariant = (
  v: ButtonVariant,
): NonNullable<ButtonProps["variant"]> => {
  const map: Partial<Record<ButtonVariant, ButtonProps["variant"]>> = {
    primary: "default",
    secondary: "secondary",
    success: "success",
    danger: "destructive",
    warning: "outline",
    info: "secondary",
    light: "ghost",
    dark: "secondary",
    link: "link",
    "outline-primary": "outline",
  };
  return map[v] ?? "outline";
};

interface Props {
  children?: React.ReactNode;
  cancelText?: string | React.ReactNode;
  cancelButtonVariant?: ButtonVariant;
  secondaryText?: string | React.ReactNode;
  confirmText?: string;
  onClose: () => void;
  onSecondaryAction?: () => void;
  onConfirm: FormEventHandler;
  confirming?: boolean;
  show: boolean;
  size?: "sm" | "lg" | "xl"; //md is default
  title?: string | React.ReactNode;
  showFooter?: boolean;
  shouldModalFocusItself?: boolean;
  showConfirmButton?: boolean;
  showSecondaryButton?: boolean;
  showCancelButton?: boolean;
  disableConfirmButton?: boolean;
  preventBackgroundClick?: boolean;
  hideCloseX?: boolean;
  contentClassName?: string;
  confirmingText?: string;
  "data-testid"?: string;
}

const Modal: FunctionComponent<Props> = ({
  children,
  cancelText = "Cancel",
  cancelButtonVariant = "link",
  confirmText = "Done",
  secondaryText,
  onClose,
  onConfirm,
  onSecondaryAction,
  confirming,
  show,
  size,
  title,
  shouldModalFocusItself = true,
  showConfirmButton = true,
  showSecondaryButton = false,
  showCancelButton = true,
  preventBackgroundClick = false,
  hideCloseX = false,
  contentClassName,
  disableConfirmButton = false,
  confirmingText = "Saving...",
  "data-testid": dataTestId,
}) => {
  return (
    <BModal
      enforceFocus={shouldModalFocusItself}
      show={show}
      onHide={onClose}
      centered
      size={size}
      backdrop={preventBackgroundClick ? "static" : true}
      className={hideCloseX ? "hide-close" : ""}
      keyboard={!hideCloseX}
      contentClassName={contentClassName}
    >
      <BModal.Header closeButton>
        {title && (
          <BModal.Title className="fw-normal" data-testid={dataTestId}>
            {title}
          </BModal.Title>
        )}
      </BModal.Header>
      <BModal.Body>{children}</BModal.Body>
      <BModal.Footer>
        {showCancelButton &&
          (typeof cancelText === "string" ? (
            <Button
              variant={mapCancelVariant(cancelButtonVariant)}
              onClick={onClose}
            >
              {cancelText}
            </Button>
          ) : (
            cancelText
          ))}
        {showSecondaryButton && (
          <Button variant="outline" onClick={onSecondaryAction}>
            {secondaryText}
          </Button>
        )}
        {showConfirmButton && (
          <Button
            data-testid="modal-confirm-button"
            variant="default"
            onClick={onConfirm}
            loading={confirming}
            disabled={disableConfirmButton}
            text={confirmingText}
          >
            {confirmText}
          </Button>
        )}
      </BModal.Footer>
    </BModal>
  );
};

const FormModal: FunctionComponent<Props> = ({
  children,
  cancelText = "Cancel",
  confirmText = "Done",
  onClose,
  onConfirm,
  confirming,
  show,
  size,
  title,
  showConfirmButton = true,
  showCancelButton = true,
  disableConfirmButton = false,
  contentClassName,
  preventBackgroundClick = false,
  confirmingText = "Saving...",
  cancelButtonVariant = "link",
}) => {
  return (
    <BModal
      show={show}
      onHide={onClose}
      centered
      size={size}
      contentClassName={contentClassName}
      backdrop={preventBackgroundClick ? "static" : true}
    >
      <FormPreventDefault onSubmit={onConfirm}>
        <BModal.Header closeButton>
          {title && (
            <BModal.Title
              data-testid={`modal-title-${typeof title === "string" ? title.toLowerCase().replace(" ", "-") : ""}`}
              className="fw-normal"
            >
              {title}
            </BModal.Title>
          )}
        </BModal.Header>
        <BModal.Body>{children}</BModal.Body>
        <BModal.Footer>
          {showCancelButton &&
            (typeof cancelText === "string" ? (
              <Button
                variant={mapCancelVariant(cancelButtonVariant)}
                onClick={onClose}
                data-testid="modal-cancel-button"
              >
                {cancelText}
              </Button>
            ) : (
              cancelText
            ))}
          {showConfirmButton && (
            <Button
              variant="default"
              type="submit"
              disabled={disableConfirmButton}
              loading={confirming}
              text={confirmingText}
              data-testid="modal-confirm-button"
            >
              {confirmText}
            </Button>
          )}
        </BModal.Footer>
      </FormPreventDefault>
    </BModal>
  );
};

export { Modal, FormModal };
